import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, GripVertical, Phone, Mail, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STAGES = [
  { key: 'new', label: 'New', color: 'bg-slate-500' },
  { key: 'contacted', label: 'Contacted', color: 'bg-sky-500' },
  { key: 'site_visit', label: 'Site Visit', color: 'bg-violet-500' },
  { key: 'design', label: 'Design', color: 'bg-amber-500' },
  { key: 'bid_sent', label: 'Bid Sent', color: 'bg-orange-500' },
  { key: 'negotiation', label: 'Negotiation', color: 'bg-pink-500' },
  { key: 'won', label: 'Won', color: 'bg-emerald-500' },
  { key: 'lost', label: 'Lost', color: 'bg-red-500' },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', city: '', source: '', notes: '' });
  const navigate = useNavigate();

  useEffect(() => { loadLeads(); }, []);
  const loadLeads = () => api.get('/leads').then(r => setLeads(r.data)).catch(() => {});

  const handleCreate = async () => {
    await api.post('/leads', form);
    toast.success('Lead created');
    setDialogOpen(false);
    setForm({ name: '', phone: '', email: '', address: '', city: '', source: '', notes: '' });
    loadLeads();
  };

  const handleStageChange = async (leadId, newStage) => {
    await api.put(`/leads/${leadId}`, { pipeline_stage: newStage });
    loadLeads();
    toast.success('Stage updated');
  };

  const handleConvertToBid = async (leadId) => {
    const r = await api.post(`/leads/${leadId}/convert-to-bid`);
    toast.success('Bid created from lead');
    navigate(`/bids/${r.data.id}`);
  };

  const grouped = {};
  STAGES.forEach(s => { grouped[s.key] = []; });
  leads.forEach(l => {
    if (grouped[l.pipeline_stage]) grouped[l.pipeline_stage].push(l);
    else grouped.new.push(l);
  });

  return (
    <div className="p-6 lg:p-8" data-testid="leads-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Leads Pipeline</h1>
          <p className="text-slate-500 text-sm mt-1">{leads.length} total leads</p>
        </div>
        <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={() => setDialogOpen(true)} data-testid="add-lead-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Lead
        </Button>
      </div>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGES.map(stage => (
            <div key={stage.key} className="w-[280px] flex-shrink-0">
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className={`w-2.5 h-2.5 rounded-full ${stage.color}`} />
                <h3 className="text-sm font-bold text-slate-700">{stage.label}</h3>
                <Badge variant="secondary" className="ml-auto text-xs font-mono">{grouped[stage.key].length}</Badge>
              </div>
              <div className="space-y-2 kanban-column bg-slate-50/60 rounded-xl p-2 min-h-[400px]">
                {grouped[stage.key].map(lead => (
                  <Card key={lead.id} className="kanban-card border-slate-100 bg-white" data-testid={`lead-card-${lead.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-sm font-semibold text-slate-800">{lead.name}</p>
                        <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
                      </div>
                      {lead.city && (
                        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                          <MapPin className="w-3 h-3" /> {lead.city}
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </div>
                      )}
                      {lead.email && (
                        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </div>
                      )}
                      {lead.notes && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{lead.notes}</p>}
                      <div className="flex items-center gap-1 mt-3">
                        <Select value={lead.pipeline_stage} onValueChange={(v) => handleStageChange(lead.id, v)}>
                          <SelectTrigger className="h-7 text-xs flex-1" data-testid={`lead-stage-select-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {!['won', 'lost'].includes(lead.pipeline_stage) && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-sky-600 text-xs"
                            onClick={() => handleConvertToBid(lead.id)} data-testid={`convert-lead-${lead.id}`}>
                            <ArrowRight className="w-3 h-3 mr-1" /> Bid
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-lead-dialog">
          <DialogHeader><DialogTitle>Add New Lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} data-testid="lead-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} data-testid="lead-form-phone" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} data-testid="lead-form-email" /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} data-testid="lead-form-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label><Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} data-testid="lead-form-city" /></div>
              <div><Label>Source</Label>
                <Select value={form.source} onValueChange={v => setForm(p => ({...p, source: v}))}>
                  <SelectTrigger data-testid="lead-form-source"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {['Referral','Website','Home Show','Social Media','Drive By','Other'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} data-testid="lead-form-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleCreate} disabled={!form.name} data-testid="lead-form-save-btn">Create Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
